import { useEffect, useRef, useState } from "react";

import { FrontendTrackingService } from "@/src/service";
import { VerticalNestedItem } from "@/src/type/strapi/component/section";

import { DisplayItem } from "../DisplayItem";
import { AnimatedItem } from "../AnimatedItem";

import styles from "./NestedItem.module.scss";

interface NestedItemProps {
  item: VerticalNestedItem;
  handleOpen?: () => void;
  handleClose?: () => void;
  childItems?: VerticalNestedItem[];
  isOutline?: boolean;
  lazy?: "lazy" | "eager";
  quality?: number;
  isExpanded?: boolean;
  expandedItem?: string;
  immediateOff?: boolean;
  fullLoad?: boolean;
  totalItems?: number;
}

function NestedItem({
  item,
  handleOpen,
  handleClose,
  isOutline,
  lazy = "eager",
  quality,
  isExpanded,
  childItems,
  expandedItem,
  immediateOff,
  fullLoad,
  totalItems,
}: NestedItemProps) {
  const containerRef = useRef<HTMLDivElement>(null);
  const [boundingClientRect, setBoundingClientRect] = useState<DOMRect | undefined>(undefined);

  useEffect(() => {
    function updateBoundingClientRect() {
      if (containerRef.current) {
        setBoundingClientRect(containerRef.current.getBoundingClientRect());
      }
    }

    updateBoundingClientRect();
    window.addEventListener("resize", updateBoundingClientRect);
    return () => window.removeEventListener("resize", updateBoundingClientRect);
  }, [expandedItem]);

  const handleContainerClick = (direction?: "opened" | "closed") => {
    FrontendTrackingService.postTrackingLog({
      action: "click",
      actionId: `VerticalNestedItem:${item.id}`,
      actionIdType: "componentName:componentId",
      actionMessage: `VerticalNestedItem ${direction ?? "opened"} with ${item.title}`,
      triggeredFrom: "VerticalNestedBaseItem.tsx:(handleContainerClick)",
    });

    if (direction === "opened") {
      if (handleOpen) {
        handleOpen();
        return;
      }
    } else if (direction === "closed") {
      if (handleClose) {
        handleClose();
        return;
      }
    }
  };

  return (
    <>
      <div
        className={styles.nestedItem}
        ref={containerRef}
      >
        <DisplayItem
          item={item}
          lazy={lazy}
          quality={quality}
          isExpanded={false}
          expandedItem={expandedItem}
          onClick={() => handleContainerClick("opened")}
          isOutline={isOutline}
          fullLoad={fullLoad}
          totalItems={totalItems}
        />
      </div>
      {!immediateOff && (
        <AnimatedItem
          item={item}
          lazy={lazy}
          quality={quality}
          isExpanded={isExpanded}
          expandedItem={expandedItem}
          childItems={childItems}
          isOutline={isOutline}
          boundingClientRect={boundingClientRect}
          onClick={() => handleContainerClick("closed")}
        />
      )}
    </>
  );
}

export default NestedItem;
